import { MenuItem, styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipProps } from 'utils/helper';

const CustomMenuItem = styled(MenuItem)(({ size, theme }) => {
    const fontStyles = {
        small: {
            minHeight: '1.5rem',
            fontSize: `${theme.typography.p3.fontSize.small}rem`,
            fontWeight: theme.typography.p3.fontWeight.regular,
            letterSpacing: `${theme.typography.p3.letterSpacing.small}rem`,
        },
        medium: {
            minHeight: '1.625rem',
            fontSize: `${theme.typography.p2.fontSize.large}rem`,
            fontWeight: theme.typography.p2.fontWeight.regular,
            letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
        },
        large: {
            minHeight: '1.75rem',
            fontSize: `${theme.typography.p1.fontSize.large}rem`,
            fontWeight: theme.typography.p1.fontWeight.regular,
            letterSpacing: `${theme.typography.p1.letterSpacing.large}rem`,
        },
    }[size];

    return {
        borderRadius: '4px',
        height: 'fit-content',
        padding: theme.spacing(1, 2),
        ...fontStyles,
        '&.Mui-focusVisible': {
            background: theme.palette.ensightful.primary[50],
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&.Mui-selected, &:hover': {
            background: theme.palette.ensightful.primary[50],
        },
        '&:active, &.Mui-selected:active': {
            background: theme.palette.ensightful.primary[200],
        },
    };
});

export default forwardRef(({ tooltip, ...rest }, ref) => {
    if (tooltip && !tooltip.component)
        throw new Error(
            `Shell: Invalid tooltip in component MenuItem. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
        );
    const { device } = useViewport();

    const mappedSize = {
        mobile: 'medium',
        tablet: 'medium',
        desktop: 'medium',
    }[device];

    const MenuItemComponent = <CustomMenuItem ref={ref} size={mappedSize} {...rest} />;

    return tooltip ? <Tooltip {...getTooltipProps(tooltip)}>{MenuItemComponent}</Tooltip> : MenuItemComponent;
});
